import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  bulkAllocateCompanyPortalEventRequests,
  bulkUnassignCompanyPortalEventRequests,
  getCompanyPortalEventRequests,
} from "../../../../../network/apis/companyPortalEventRequests";
import { updateCompanyPortalEvent } from "../../../../../network/apis/companyPortalEvents";
import {
  bulkAssign,
  getRequestsRequest,
  updateEventRequest,
  updateSupervisorEventRequest,
} from "../../../../../store/Requests/actions";
import {
  showErrorMsg,
  showSuccessMsg,
  showWarningMsg,
} from "../../../../../utils/Helpers";
import messages from "../../../../../assets/locale/messages";
import { normalizeEventRequests } from "../../Applicants/eventRequestMappers";
import {
  buildZoneApplicantsParams,
  buildZoneApplicantsSearchParams,
  isAdminZoneApplicantsPortal,
} from "../portal";

const isPartialSuccess = (response) => response?.status === 207;

const reportBulkResult = (response, successToast) => {
  const message = response?.data?.message;
  if (isPartialSuccess(response)) {
    showWarningMsg(message);
    return;
  }
  if (successToast) {
    showSuccessMsg(successToast.message, true, successToast.title);
    return;
  }
  showSuccessMsg(message, true);
};

const buildCountTitle = (count, copy) =>
  count === 1
    ? copy.successTitleSingle
    : copy.successTitle.replace("{count}", count);

// The unassign toast is ours, not the API's: it counts the applicants and only
// mentions the zone reset when the zone is left empty.
const buildUnassignToast = (count, willClearZone, copy) => ({
  title: buildCountTitle(count, copy),
  message: willClearZone ? copy.successZoneCleared : "",
});

/**
 * Zone applicants list + staffing mutations for both portals.
 *
 * Admin runs through the existing `store/Requests` sagas; the company portal has
 * no zone-applicants slice, so it calls the portal endpoints directly. Either
 * way a mutation reloads the list by calling `refresh` on success — the admin
 * sagas through their `onSuccess` callback, the company calls straight through.
 */
const useZoneApplicants = ({
  eventId,
  zoneId,
  portalVariant,
  page,
  pageSize,
  search,
  roleId,
  gender,
  lang,
  sharedLabels,
  onRefreshEvent,
}) => {
  const isAdmin = isAdminZoneApplicantsPortal(portalVariant);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loader);
  const adminList = useSelector((state) => state.requests.zoneApplicants.list);
  const adminMeta = useSelector((state) => state.requests.zoneApplicants.meta);

  const [companyList, setCompanyList] = useState([]);
  const [companyMeta, setCompanyMeta] = useState({});
  const [reloadToken, setReloadToken] = useState(0);

  const listParams = useMemo(
    () => ({
      ...buildZoneApplicantsParams({ eventId, zoneId }),
      page,
      items: pageSize,
      role_id: roleId,
      gender,
      ...buildZoneApplicantsSearchParams(search),
    }),
    [eventId, zoneId, page, pageSize, roleId, gender, search]
  );

  // The mutation sagas refresh through `getEventRequestList({ id, params })` and
  // re-fetch the event when `event_id` is present — a flat query hits
  // `/events/undefined/event_requests`, so keep all three keys.
  const adminMutationListParams = useMemo(
    () => ({ id: eventId, params: { page: -1 }, event_id: eventId }),
    [eventId]
  );

  useEffect(() => {
    if (!isAdmin) return;
    dispatch(getRequestsRequest({ ...listParams, responseType: "zoneApplicants" }));
  }, [dispatch, isAdmin, listParams, reloadToken]);

  useEffect(() => {
    if (isAdmin || !eventId) return undefined;

    let mounted = true;

    getCompanyPortalEventRequests(listParams)
      .then((response) => {
        if (!mounted) return;
        setCompanyList(response.data?.data || []);
        setCompanyMeta({
          currentPage: Number(response.headers["current-page"] || 1),
          count: Number(response.headers["total-count"] || 0),
          pages: Number(response.headers["total-pages"] || 1),
        });
      })
      .catch(() => {
        if (!mounted) return;
        setCompanyList([]);
        setCompanyMeta({});
      });

    return () => {
      mounted = false;
    };
  }, [isAdmin, eventId, listParams, reloadToken]);

  // Both portals refresh the same way: every mutation calls this on success.
  // Admin passes it to the sagas as `onSuccess`; the company portal calls it
  // directly. `onRefreshEvent` is the company portal's event reload — the admin
  // sagas already re-fetch the event themselves.
  const refresh = useCallback(() => {
    setReloadToken((token) => token + 1);
    onRefreshEvent?.();
  }, [onRefreshEvent]);

  const assignApplicants = useCallback(
    async (eventRequestIds) => {
      const successToast = {
        title: buildCountTitle(
          eventRequestIds.length,
          messages[lang].zoneApplicants.assignModal
        ),
        message: "",
      };

      if (isAdmin) {
        dispatch(
          bulkAssign({
            data: { zone_id: +zoneId, event_request_ids: eventRequestIds },
            listParams: adminMutationListParams,
            successToast,
            onSuccess: refresh,
          })
        );
        return;
      }

      try {
        const response = await bulkAllocateCompanyPortalEventRequests({
          event_request_ids: eventRequestIds,
          zone_id: +zoneId,
        });
        reportBulkResult(response, successToast);
        refresh();
      } catch (error) {
        showErrorMsg(error.response?.data?.message);
      }
    },
    [isAdmin, dispatch, zoneId, adminMutationListParams, refresh, lang]
  );

  const unassignApplicant = useCallback(
    async (row, { willClearZone } = {}) => {
      const successToast = buildUnassignToast(
        1,
        willClearZone,
        messages[lang].zoneApplicants.unassignModal
      );

      if (isAdmin) {
        dispatch(
          updateEventRequest({
            data: { zone_id: null },
            id: row?.requestId,
            listParams: adminMutationListParams,
            successToast,
            onSuccess: refresh,
          })
        );
        return;
      }

      try {
        const response = await bulkUnassignCompanyPortalEventRequests({
          event_request_ids: [row?.requestId],
        });
        reportBulkResult(response, successToast);
        refresh();
      } catch (error) {
        showErrorMsg(error.response?.data?.message);
      }
    },
    [isAdmin, dispatch, adminMutationListParams, refresh, lang]
  );

  const changeSupervisor = useCallback(
    async (supervisorId, { isChange } = {}) => {
      // Staffing an empty zone gets its own toast; replacing one keeps the
      // API's generic "updated" message.
      const successToast = isChange
        ? undefined
        : {
            message:
              messages[lang].zoneApplicants.supervisorModal.assignSuccess,
          };

      if (isAdmin) {
        dispatch(
          updateSupervisorEventRequest({
            data: { supervisor_id: +supervisorId },
            id: zoneId,
            listParams: adminMutationListParams,
            eventId,
            successToast,
            onSuccess: refresh,
          })
        );
        return;
      }

      // The company portal has no `/zones` endpoint — the supervisor is saved
      // through the event's nested zone attributes, same as the event form.
      try {
        const response = await updateCompanyPortalEvent({
          id: eventId,
          data: {
            zones_attributes: [
              { id: +zoneId, supervisor_id: +supervisorId },
            ],
          },
        });
        showSuccessMsg(successToast?.message || response.data?.message, true);
        refresh();
      } catch (error) {
        showErrorMsg(error.response?.data?.message);
      }
    },
    [isAdmin, dispatch, zoneId, eventId, adminMutationListParams, refresh, lang]
  );

  // Clearing the supervisor is the same call as changing them, with a null id —
  // the toast is ours because the API message is a generic "updated".
  const unassignSupervisor = useCallback(async () => {
    const successMessage =
      messages[lang].zoneApplicants.unassignSupervisorModal.success;

    if (isAdmin) {
      dispatch(
        updateSupervisorEventRequest({
          data: { supervisor_id: null },
          id: zoneId,
          listParams: adminMutationListParams,
          eventId,
          successToast: { message: successMessage },
          onSuccess: refresh,
        })
      );
      return;
    }

    try {
      await updateCompanyPortalEvent({
        id: eventId,
        data: {
          zones_attributes: [{ id: +zoneId, supervisor_id: null }],
        },
      });
      showSuccessMsg(successMessage, true);
      refresh();
    } catch (error) {
      showErrorMsg(error.response?.data?.message);
    }
  }, [isAdmin, dispatch, zoneId, eventId, adminMutationListParams, refresh, lang]);

  const rows = useMemo(() => {
    const source = isAdmin ? adminList : companyList;

    return normalizeEventRequests(source || [], lang, sharedLabels).map((row) => ({
      ...row,
      genderLabel: row.gender
        ? sharedLabels.genderOptions[row.gender] || row.gender
        : "-",
    }));
  }, [isAdmin, adminList, companyList, lang, sharedLabels]);

  return {
    rows,
    meta: isAdmin ? adminMeta : companyMeta,
    isLoading,
    assignApplicants,
    unassignApplicant,
    changeSupervisor,
    unassignSupervisor,
  };
};

export default useZoneApplicants;
